import type {
  CreatePurchaseOrderPayload,
  PurchaseOrderDetail,
  PurchaseOrderLineSuggestion,
  PurchaseOrderListRow,
  UpdatePurchaseOrderDraftPayload,
} from "@/modules/inventory/domain/types";
import type { PurchaseOrderStatus } from "@/server/platform/db/schema/purchase-orders";
import { config } from "@/shared/lib/config";
import { fetcher } from "@/shared/lib/fetcher";

export type PurchaseOrdersListParams = {
  status?: PurchaseOrderStatus;
  supplierId?: string;
  branchId?: string;
  orderDateFrom?: string;
  orderDateTo?: string;
  poNo?: string;
  limit?: number;
  offset?: number;
};

export type ReceivePurchaseOrderPayload = {
  receivedAt?: string;
  note?: string | null;
  lines: Array<{
    poLineId: string;
    qty: number;
    lotNo: string;
    expiryDate?: string | null;
  }>;
};

export const inventoryPurchaseOrdersApi = {
  async suggestedLines(params: {
    supplierId?: string;
    limit?: number;
  } = {}): Promise<PurchaseOrderLineSuggestion[]> {
    const searchParams = new URLSearchParams();
    if (params.supplierId) searchParams.set("supplierId", params.supplierId);
    if (params.limit != null) searchParams.set("limit", String(params.limit));
    const qs = searchParams.toString();
    const res = await fetcher.get<{ items: PurchaseOrderLineSuggestion[] }>(
      `${config.apiUrl}/inventory/purchase-orders/suggested-lines${qs ? `?${qs}` : ""}`,
    );
    return res.items;
  },

  async list(
    params: PurchaseOrdersListParams = {},
  ): Promise<{ items: PurchaseOrderListRow[]; total: number }> {
    const searchParams = new URLSearchParams();
    if (params.status) searchParams.set("status", params.status);
    if (params.supplierId) searchParams.set("supplierId", params.supplierId);
    if (params.branchId) searchParams.set("branchId", params.branchId);
    if (params.orderDateFrom) {
      searchParams.set("orderDateFrom", params.orderDateFrom);
    }
    if (params.orderDateTo) {
      searchParams.set("orderDateTo", params.orderDateTo);
    }
    if (params.poNo?.trim()) searchParams.set("poNo", params.poNo.trim());
    if (params.limit != null) searchParams.set("limit", String(params.limit));
    if (params.offset != null) searchParams.set("offset", String(params.offset));
    const qs = searchParams.toString();
    return fetcher.get(
      `${config.apiUrl}/inventory/purchase-orders${qs ? `?${qs}` : ""}`,
    );
  },

  async get(id: string): Promise<PurchaseOrderDetail> {
    const res = await fetcher.get<{ item: PurchaseOrderDetail }>(
      `${config.apiUrl}/inventory/purchase-orders/${id}`,
    );
    return res.item;
  },

  async create(
    payload: CreatePurchaseOrderPayload,
  ): Promise<PurchaseOrderDetail> {
    const res = await fetcher.post<{ item: PurchaseOrderDetail }>(
      `${config.apiUrl}/inventory/purchase-orders`,
      payload,
    );
    return res.item;
  },

  async updateDraft(
    id: string,
    payload: UpdatePurchaseOrderDraftPayload,
  ): Promise<PurchaseOrderDetail> {
    const res = await fetcher.patch<{ item: PurchaseOrderDetail }>(
      `${config.apiUrl}/inventory/purchase-orders/${id}`,
      payload,
    );
    return res.item;
  },

  async submit(id: string): Promise<PurchaseOrderDetail> {
    const res = await fetcher.post<{ item: PurchaseOrderDetail }>(
      `${config.apiUrl}/inventory/purchase-orders/${id}/submit`,
      {},
    );
    return res.item;
  },

  async approve(id: string): Promise<PurchaseOrderDetail> {
    const res = await fetcher.post<{ item: PurchaseOrderDetail }>(
      `${config.apiUrl}/inventory/purchase-orders/${id}/approve`,
      {},
    );
    return res.item;
  },

  async reject(id: string): Promise<PurchaseOrderDetail> {
    const res = await fetcher.post<{ item: PurchaseOrderDetail }>(
      `${config.apiUrl}/inventory/purchase-orders/${id}/reject`,
      {},
    );
    return res.item;
  },

  async cancel(id: string): Promise<PurchaseOrderDetail> {
    const res = await fetcher.post<{ item: PurchaseOrderDetail }>(
      `${config.apiUrl}/inventory/purchase-orders/${id}/cancel`,
      {},
    );
    return res.item;
  },

  async close(id: string): Promise<PurchaseOrderDetail> {
    const res = await fetcher.post<{ item: PurchaseOrderDetail }>(
      `${config.apiUrl}/inventory/purchase-orders/${id}/close`,
      {},
    );
    return res.item;
  },

  async receive(
    id: string,
    payload: ReceivePurchaseOrderPayload,
  ): Promise<PurchaseOrderDetail> {
    const res = await fetcher.post<{ item: PurchaseOrderDetail }>(
      `${config.apiUrl}/inventory/purchase-orders/${id}/receive`,
      payload,
    );
    return res.item;
  },
};

export function formatPurchaseOrderApiError(e: unknown): string {
  const msg = (e as Error)?.message;
  const map: Record<string, string> = {
    NOT_FOUND: "ບໍ່ພົບໃບສັ່ງຊື້",
    INVALID_STATUS: "ສະຖານະ PO ບໍ່ອະນຸຍາດໃຫ້ດຳເນີນການນີ້",
    INVALID_BRANCH_NOT_HUB: "PO ຕ້ອງຮັບທີ່ Hub ເທົ່ານັ້ນ",
    QTY_EXCEEDS_ORDERED: "ປະລິມານຮັບເກີນຈຳນວນທີ່ສັ່ງ",
    SUPPLIER_INACTIVE: "ຜູ້ສະໜອງຖືກປິດໃຊ້ — ບໍ່ສາມາດສ້າງ PO ໃໝ່",
    SUPPLIER_NOT_FOUND: "ບໍ່ພົບຜູ້ສະໜອງ",
    ITEM_NOT_FOUND: "ບໍ່ພົບລາຍການສິນຄ້າ",
    ITEM_INACTIVE: "ລາຍການສິນຄ້າຖືກປິດໃຊ້",
    EMPTY_LINES: "ຕ້ອງມີຢ່າງໜ້ອຍ 1 ບັນທັດ",
    LINES_REQUIRED: "ຕ້ອງມີຢ່າງໜ້ອຍ 1 ບັນທັດ",
    NO_DISTRIBUTION_HUB: "ຍັງບໍ່ມີສາຂາ Hub — ຕັ້ງຄ່າ distribution hub ກ່ອນ",
    LINE_NOT_FOUND: "ບໍ່ພົບບັນທັດ PO",
  };
  if (msg && map[msg]) return map[msg];
  return msg ?? "ຄໍາຮ້ອງຂໍລົ້ມເຫຼວ";
}
